import { useRef, useState, useEffect } from "react";
import { Mic, Square, Send, Loader2, Volume2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useI18n } from "../lib/i18n";
import { errMsg } from "../lib/api";
import { canRecord, startRecording, transcribe, streamChat, ttsUrl } from "../lib/voice";

const newSession = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}-${Math.random()}`);

const LangToggle = ({ value, onChange, testId }) => (
  <div className="inline-flex rounded-xl bg-slate-100 p-1" data-testid={testId}>
    {["en", "ar"].map((l) => (
      <button key={l} data-testid={`${testId}-${l}`} onClick={() => onChange(l)}
        className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${value === l ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
        {l === "en" ? "English" : "العربية"}
      </button>
    ))}
  </div>
);

const Bubble = ({ m, i, onPlay }) => (
  <div data-testid={`voice-message-${i}`} className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "ms-auto bg-slate-900 text-white" : "bg-slate-100 text-slate-800"}`}>
    <p dir="auto">{m.text || "…"}</p>
    {(m.meta || m.asrMs) && (
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] opacity-70">
        {m.asrMs && <span>ASR {m.asrMs}ms</span>}
        {m.meta?.first_token_ms && <span>LLM 1st {m.meta.first_token_ms}ms</span>}
        {m.meta?.ttsMs && <span>TTS {m.meta.ttsMs}ms</span>}
        {m.audio && (
          <button data-testid={`voice-replay-${i}`} onClick={() => onPlay(m.audio)} className="inline-flex items-center gap-1 underline">
            <Volume2 className="h-3 w-3" />replay
          </button>
        )}
      </div>
    )}
  </div>
);

export const VoiceConsole = () => {
  const { t, lang } = useI18n();
  const [inLang, setInLang] = useState(lang);
  const [outLang, setOutLang] = useState(lang);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("idle");
  const [err, setErr] = useState("");
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const session = useRef(newSession());

  useEffect(() => { endRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const updateLast = (patch) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], ...patch }; return c; });

  const play = (u) => {
    const a = audioRef.current;
    a.src = u;
    setPhase("speaking");
    a.play().catch(() => setPhase("idle"));
  };

  const send = async (content, asrMs) => {
    if (!content?.trim()) { setPhase("idle"); return; }
    setErr("");
    setMsgs((m) => [...m, { role: "user", text: content, asrMs }, { role: "assistant", text: "" }]);
    setText("");
    setPhase("thinking");
    try {
      const { text: reply, meta } = await streamChat(
        { session_id: session.current, text: content, language: inLang, reply_language: outLang },
        (partial) => updateLast({ text: partial })
      );
      const t0 = performance.now();
      const u = await ttsUrl(reply, outLang);
      updateLast({ meta: { ...meta, ttsMs: Math.round(performance.now() - t0) }, audio: u });
      play(u);
    } catch (e) { setErr(errMsg(e)); setPhase("idle"); }
  };

  const toggleRec = async () => {
    if (phase === "recording") {
      const blob = await recRef.current.stop();
      setPhase("transcribing");
      try { const d = await transcribe(blob, inLang); await send(d.text, d.ms); } catch (e) { setErr(errMsg(e)); setPhase("idle"); }
      return;
    }
    try { recRef.current = await startRecording(); setErr(""); setPhase("recording"); } catch (_) { setErr(t("noMic")); }
  };

  const busy = ["transcribing", "thinking"].includes(phase);
  const phaseLabel = { recording: t("listening"), transcribing: t("transcribing"), thinking: t("thinking") }[phase];

  return (
    <div className="flex h-full min-h-[420px] flex-col gap-4" data-testid="voice-console">
      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <span className="flex items-center gap-2">{t("inputLang")} <LangToggle value={inLang} onChange={setInLang} testId="voice-input-lang" /></span>
        <span className="flex items-center gap-2">{t("replyLang")} <LangToggle value={outLang} onChange={setOutLang} testId="voice-reply-lang" /></span>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/60 p-4" data-testid="voice-messages">
        {msgs.length === 0 && <p className="text-sm text-slate-500">{t("voiceSub")}</p>}
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} onPlay={play} />)}
        <div ref={endRef} />
      </div>
      {err && <p data-testid="voice-error" className="text-xs text-rose-700">{err}</p>}
      <div className="flex items-center gap-2">
        <button data-testid="voice-assistant-mic-button" onClick={toggleRec} disabled={busy || !canRecord()}
          aria-label={phase === "recording" ? t("stopRec") : t("holdToTalk")} title={canRecord() ? "" : t("noMic")}
          className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white transition-transform active:scale-95 disabled:opacity-40 ${phase === "recording" ? "bg-rose-600" : "bg-sky-600 hover:bg-sky-700"}`}>
          {phase === "recording" && <span className="absolute inset-0 animate-ping rounded-full bg-rose-400 opacity-40" />}
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : phase === "recording" ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
        </button>
        <form className="flex flex-1 items-center gap-2" onSubmit={(e) => { e.preventDefault(); send(text); }}>
          <Input data-testid="voice-text-input" dir="auto" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("typeMessage")} className="h-12 rounded-xl" disabled={busy} />
          <Button data-testid="voice-send-button" type="submit" disabled={busy || !text.trim()} className="h-12 rounded-xl bg-slate-900 px-4 hover:bg-slate-800">
            <Send className="h-4 w-4 rtl:rotate-180" />
          </Button>
        </form>
      </div>
      {phaseLabel && <p data-testid="voice-phase" className="text-xs font-medium text-sky-700">{phaseLabel}</p>}
      <audio ref={audioRef} playsInline onEnded={() => setPhase("idle")} className="hidden" />
    </div>
  );
};
